import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DashboardLayout from '../../components/layout/DashboardLayout';
import {
  listBreweryActivityEmails,
  prepareBreweryActivityEmails,
  previewBreweryActivityEmail,
  sendBreweryActivityEmail,
} from '../../services/breweryActivityEmails';

const STATUS_FILTER_ALL = 'all';

const PAGE_CONTENT_SX = {
  py: { xs: 2, md: 3 },
  px: { xs: 2, sm: 3 },
};

function statusChipColor(status) {
  if (status === 'sent') {
    return 'success';
  }
  if (status === 'failed') {
    return 'error';
  }
  return 'warning';
}

function sanitizePreviewHtml(html) {
  if (typeof html !== 'string' || html.length === 0) {
    return '';
  }

  const config = {
    ALLOWED_TAGS: ['p', 'strong', 'a', 'ul', 'li', 'br', 'em', 'span'],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
  };

  if (typeof DOMPurify.addHook !== 'function') {
    return DOMPurify.sanitize(html, config);
  }

  const applyAnchorRelPolicy = (node) => {
    if (node.tagName === 'A' && node.hasAttribute('target')) {
      node.setAttribute('rel', 'noopener noreferrer');
    }
  };

  DOMPurify.addHook('afterSanitizeAttributes', applyAnchorRelPolicy);
  const sanitized = DOMPurify.sanitize(html, config);
  DOMPurify.removeHook('afterSanitizeAttributes', applyAnchorRelPolicy);

  return sanitized;
}

function renderInfoCellContent(item) {
  if (item.status === 'sent') {
    const trackingLabels = formatTrackingSummary(item.tracking);
    if (trackingLabels.length > 0) {
      return (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {trackingLabels.map((label) => (
            <Chip key={label} size="small" variant="outlined" label={label} />
          ))}
        </Stack>
      );
    }

    return (
      <Typography variant="body2" color="text.secondary">
        Pending
      </Typography>
    );
  }

  if (item.status === 'failed') {
    const errorText = typeof item.lastError === 'string' ? item.lastError.trim() : '';
    if (errorText.length > 0) {
      return (
        <Typography variant="body2" color="error">
          {errorText}
        </Typography>
      );
    }

    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <Typography variant="body2" color="text.secondary">
      —
    </Typography>
  );
}

function formatTrackingSummary(tracking) {
  if (tracking == null || typeof tracking !== 'object') {
    return [];
  }

  const entries = [
    ['Delivered', tracking.deliveredAt],
    ['Clicked', tracking.clickedAt],
    ['Bounced', tracking.bouncedAt],
    ['Complained', tracking.complainedAt],
    ['Unsubscribed', tracking.unsubscribedAt],
  ];

  return entries
    .filter(([, timestamp]) => typeof timestamp === 'string' && timestamp.length > 0)
    .map(([label]) => label);
}

function EmailPreviewDialog({
  open,
  selectedItem,
  preview,
  isLoadingPreview,
  isSending,
  sendErrorMessage,
  canSend,
  onClose,
  onSend,
}) {
  const sanitizedHtml = preview?.html ? sanitizePreviewHtml(preview.html) : '';

  const handleDialogClose = useCallback(() => {
    if (isSending) {
      return;
    }
    onClose();
  }, [isSending, onClose]);

  return (
    <Dialog open={open} onClose={handleDialogClose} fullWidth maxWidth="md">
      <DialogTitle>Email preview</DialogTitle>
      <DialogContent dividers>
        {sendErrorMessage ? <Alert severity="error" sx={{ mb: 2 }}>{sendErrorMessage}</Alert> : null}
        {selectedItem ? (
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {selectedItem.breweryName} · {selectedItem.periodKey} · {selectedItem.recipientEmail}
          </Typography>
        ) : null}
        {isLoadingPreview ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : preview ? (
          <Stack spacing={1.5}>
            <Typography variant="subtitle1">{preview.subject}</Typography>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
                maxHeight: 480,
                overflow: 'auto',
              }}
              dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
            />
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Unable to load preview for this email.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDialogClose} disabled={isSending}>Close</Button>
        {canSend ? (
          <Button
            variant="contained"
            onClick={onSend}
            disabled={isSending || isLoadingPreview || !preview}
            loading={isSending}
          >
            Send
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}

function BreweryActivityEmailRow({
  item,
  selectedEmailId,
  onPreview,
  onSend,
}) {
  const handlePreviewClick = useCallback(() => {
    onPreview(item.id);
  }, [item.id, onPreview]);

  const handleSendClick = useCallback(() => {
    onSend(item.id);
  }, [item.id, onSend]);

  const canSend = item.status === 'ready' || item.status === 'failed';

  return (
    <TableRow selected={item.id === selectedEmailId} hover>
      <TableCell>{item.breweryName}</TableCell>
      <TableCell>{item.recipientEmail}</TableCell>
      <TableCell>{item.periodKey}</TableCell>
      <TableCell>{item.metrics?.reviews ?? 0}</TableCell>
      <TableCell>
        <Chip size="small" label={item.status} color={statusChipColor(item.status)} />
      </TableCell>
      <TableCell>{renderInfoCellContent(item)}</TableCell>
      <TableCell align="center">
        <Stack direction="row" spacing={1} justifyContent="center">
          <Button size="small" onClick={handlePreviewClick}>
            Preview
          </Button>
          {canSend ? (
            <Button size="small" variant="contained" onClick={handleSendClick}>
              Send
            </Button>
          ) : null}
        </Stack>
      </TableCell>
    </TableRow>
  );
}

function BreweryActivityEmailToolbar({
  awaitingCount,
  sendBudget,
  statusFilter,
  onStatusFilterChange,
  onRefresh,
  onPrepare,
  isLoading,
  isWorking,
}) {
  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
        <Typography variant="body1">
          {awaitingCount} awaiting review
        </Typography>
        {sendBudget ? (
          <Typography variant="body2" color="text.secondary">
            Sent today: {sendBudget.sentCount}/{sendBudget.dailyCap}
          </Typography>
        ) : null}
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="email-status-filter-label">Status</InputLabel>
          <Select
            labelId="email-status-filter-label"
            label="Status"
            value={statusFilter}
            onChange={onStatusFilterChange}
          >
            <MenuItem value="ready">Ready to send</MenuItem>
            <MenuItem value="sent">Sent</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value={STATUS_FILTER_ALL}>All</MenuItem>
          </Select>
        </FormControl>
        <Button variant="outlined" onClick={onRefresh} disabled={isLoading || isWorking}>
          Refresh
        </Button>
        <Button variant="contained" onClick={onPrepare} disabled={isWorking}>
          Prepare period
        </Button>
      </Stack>
    </Paper>
  );
}

function BreweryActivityEmailsPage() {
  const [items, setItems] = useState([]);
  const [awaitingCount, setAwaitingCount] = useState(0);
  const [sendBudget, setSendBudget] = useState(null);
  const [statusFilter, setStatusFilter] = useState('ready');
  const [selectedEmailId, setSelectedEmailId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isWorking, setIsWorking] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const previewRequestIdRef = useRef(0);

  const selectedItem = useMemo(
    () => items.find((item) => item.id === selectedEmailId) ?? null,
    [items, selectedEmailId],
  );

  const canSendSelectedItem = selectedItem?.status === 'ready' || selectedItem?.status === 'failed';

  const loadEmails = useCallback(async ({
    clearMessages = true,
    preserveErrorOnFailure = false,
  } = {}) => {
    setIsLoading(true);
    if (clearMessages) {
      setErrorMessage('');
    }
    try {
      const filters = { limit: 100 };
      if (statusFilter !== STATUS_FILTER_ALL) {
        filters.status = statusFilter;
      }
      const result = await listBreweryActivityEmails(filters);
      setItems(Array.isArray(result?.items) ? result.items : []);
      setAwaitingCount(Number(result?.awaitingCount) || 0);
      setSendBudget(result?.sendBudget ?? null);
    } catch (loadError) {
      if (!preserveErrorOnFailure) {
        setErrorMessage(
          typeof loadError?.message === 'string'
            ? loadError.message
            : 'Unable to load brewery activity emails.',
        );
      }
      setItems([]);
      setAwaitingCount(0);
      setSendBudget(null);
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadEmails();
  }, [loadEmails]);

  const loadPreview = useCallback(async (emailId) => {
    const requestId = previewRequestIdRef.current + 1;
    previewRequestIdRef.current = requestId;
    setSelectedEmailId(emailId);
    setPreview(null);
    setIsLoadingPreview(true);
    setErrorMessage('');
    try {
      const result = await previewBreweryActivityEmail(emailId);
      if (previewRequestIdRef.current !== requestId) {
        return;
      }
      setPreview(result?.preview ?? null);
    } catch (previewError) {
      if (previewRequestIdRef.current !== requestId) {
        return;
      }
      setErrorMessage(
        typeof previewError?.message === 'string'
          ? previewError.message
          : 'Unable to preview email.',
      );
    } finally {
      if (previewRequestIdRef.current === requestId) {
        setIsLoadingPreview(false);
      }
    }
  }, []);

  const handleOpenPreviewModal = useCallback(async (emailId) => {
    setPreviewModalOpen(true);
    setErrorMessage('');
    setSuccessMessage('');
    await loadPreview(emailId);
  }, [loadPreview]);

  const handleClosePreviewModal = useCallback(() => {
    setPreviewModalOpen(false);
    setIsLoadingPreview(false);
    setPreview(null);
  }, []);

  const closePreviewModalAfterSend = useCallback(() => {
    setPreviewModalOpen(false);
    setIsLoadingPreview(false);
    setPreview(null);
  }, []);

  const handlePrepare = useCallback(async () => {
    setIsWorking(true);
    setErrorMessage('');
    setSuccessMessage('');
    try {
      const result = await prepareBreweryActivityEmails({});
      setSuccessMessage(
        `Prepared ${result?.preparedCount ?? 0} email(s) for ${result?.periodKey ?? 'period'}.`,
      );
      await loadEmails();
    } catch (prepareError) {
      setErrorMessage(
        typeof prepareError?.message === 'string'
          ? prepareError.message
          : 'Unable to prepare brewery activity emails.',
      );
    } finally {
      setIsWorking(false);
    }
  }, [loadEmails]);

  const handleSend = useCallback(async () => {
    if (!selectedEmailId) {
      return;
    }
    setIsSending(true);
    setErrorMessage('');
    setSuccessMessage('');
    let sendFailed = false;
    try {
      await sendBreweryActivityEmail(selectedEmailId);
      closePreviewModalAfterSend();
      setSuccessMessage('Email sent successfully.');
    } catch (sendError) {
      sendFailed = true;
      setErrorMessage(
        typeof sendError?.message === 'string'
          ? sendError.message
          : 'Unable to send email.',
      );
    } finally {
      setIsSending(false);
      await loadEmails({ clearMessages: false, preserveErrorOnFailure: sendFailed });
    }
  }, [closePreviewModalAfterSend, loadEmails, selectedEmailId]);

  const handleStatusFilterChange = useCallback((event) => {
    setStatusFilter(event.target.value);
  }, []);

  return (
    <DashboardLayout title="Email Notifications">
      <Box sx={PAGE_CONTENT_SX}>
        <Stack spacing={2}>
          <Typography variant="body1" color="text.secondary">
            Review and send monthly brewery activity emails to claimed brewery admins.
          </Typography>

          {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
          {successMessage ? <Alert severity="success">{successMessage}</Alert> : null}

          <BreweryActivityEmailToolbar
            awaitingCount={awaitingCount}
            sendBudget={sendBudget}
            statusFilter={statusFilter}
            onStatusFilterChange={handleStatusFilterChange}
            onRefresh={loadEmails}
            onPrepare={handlePrepare}
            isLoading={isLoading}
            isWorking={isWorking}
          />

          <Paper sx={{ p: 1 }}>
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Brewery</TableCell>
                    <TableCell>Recipient</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell>Reviews</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Info</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <BreweryActivityEmailRow
                      key={item.id}
                      item={item}
                      selectedEmailId={selectedEmailId}
                      onPreview={handleOpenPreviewModal}
                      onSend={handleOpenPreviewModal}
                    />
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Stack>
      </Box>

      <EmailPreviewDialog
        open={previewModalOpen}
        selectedItem={selectedItem}
        preview={preview}
        isLoadingPreview={isLoadingPreview}
        isSending={isSending}
        sendErrorMessage={previewModalOpen ? errorMessage : ''}
        canSend={canSendSelectedItem}
        onClose={handleClosePreviewModal}
        onSend={handleSend}
      />
    </DashboardLayout>
  );
}

export default BreweryActivityEmailsPage;
